<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import Preview from "../../base-components/Preview";
import Table from "../../base-components/Table";
import { FormLabel, FormInput } from "../../base-components/Form";
import FormSelect from "../../base-components/Form/FormSelect.vue";
import Button from "../../base-components/Button";
import LoadingIcon from "../../base-components/LoadingIcon";
import { useLogAccionesStore } from "../../services/logAcciones";
import type { LogAcciones } from "@fixeduy/restaurantestypes";
import { useUsuariosStore } from "../../services/usuarios";
import { isString } from "lodash";

const logStore = useLogAccionesStore();
const usuariosStore = useUsuariosStore();

const OPCIONES_MODULO: { value: LogAcciones["modulo"] | ""; label: string }[] =
  [
    { value: "", label: "Todos los módulos" },
    { value: "comandas", label: "Comandas" },
    { value: "pedidos", label: "Pedidos" },
    { value: "turnos", label: "Turnos" },
    { value: "mesas", label: "Mesas" },
    { value: "productos", label: "Productos" },
    { value: "categorias", label: "Categorías" },
    { value: "usuarios", label: "Usuarios" },
    { value: "opciones", label: "Opciones" },
    { value: "opcionesTicketeras", label: "Ticketeras" }
  ];

const etiquetaModulo = (m: LogAcciones["modulo"]) =>
  OPCIONES_MODULO.find(o => o.value === m)?.label ?? m;

const hoy = () => {
  const d = new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
};

const haceDias = (dias: number) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
};

const moduloFiltro = ref<LogAcciones["modulo"] | "">("");
const fechaDesdeStr = ref(haceDias(7));
const fechaHastaStr = ref(hoy());

const msInicioDiaLocal = (yyyyMmDd: string) =>
  new Date(`${yyyyMmDd}T00:00:00`).getTime();

const msFinDiaLocal = (yyyyMmDd: string) =>
  new Date(`${yyyyMmDd}T23:59:59.999`).getTime();

const formatoFechaHora = (ms: number) =>
  new Date(ms).toLocaleString("es-UY", {
    dateStyle: "short",
    timeStyle: "short"
  });

const buscar = async () => {
  await logStore.buscarLogs({
    modulo: moduloFiltro.value,
    fechaDesdeMs: msInicioDiaLocal(fechaDesdeStr.value),
    fechaHastaMs: msFinDiaLocal(fechaHastaStr.value)
  });
};

const sinResultados = computed(
  () => !logStore.cargandoConsulta && logStore.logsConsulta.length === 0
);

onMounted(() => {
  void buscar();
});
</script>

<template>
  <Preview class="mt-5 box">
    <div class="p-5 space-y-4">
      <h2 class="text-base font-medium">Log de acciones</h2>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        Registros por módulo y rango de fechas (hasta 500 entradas más recientes
        en el período).
      </p>

      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12 md:col-span-4">
          <FormLabel htmlFor="log-acciones-modulo">Módulo</FormLabel>
          <FormSelect
            id="log-acciones-modulo"
            v-model="moduloFiltro"
            class="w-full"
          >
            <option
              v-for="opt in OPCIONES_MODULO"
              :key="opt.value === '' ? 'todos' : opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </FormSelect>
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormLabel htmlFor="log-acciones-desde">Desde</FormLabel>
          <FormInput
            id="log-acciones-desde"
            v-model="fechaDesdeStr"
            type="date"
            class="w-full"
          />
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormLabel htmlFor="log-acciones-hasta">Hasta</FormLabel>
          <FormInput
            id="log-acciones-hasta"
            v-model="fechaHastaStr"
            type="date"
            class="w-full"
          />
        </div>
        <div class="col-span-12 md:col-span-2 flex items-end">
          <Button
            variant="primary"
            class="w-full"
            :disabled="logStore.cargandoConsulta"
            @click="buscar"
          >
            Buscar
          </Button>
        </div>
      </div>
    </div>

    <div class="px-5 pb-5 min-w-0">
      <Preview.Panel>
        <div class="overflow-x-auto min-w-0 -mx-5 px-5">
          <Table hover class="min-w-[720px]">
            <Table.Thead>
              <Table.Tr>
                <Table.Th class="whitespace-nowrap">Fecha y hora</Table.Th>
                <Table.Th class="whitespace-nowrap">Módulo</Table.Th>
                <Table.Th class="whitespace-nowrap">Usuario</Table.Th>
                <!-- <Table.Th class="whitespace-nowrap">Mail</Table.Th> -->
                <Table.Th>Acción</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr v-if="logStore.cargandoConsulta">
                <Table.Td colspan="5">
                  <div class="w-8 mx-auto">
                    <LoadingIcon icon="oval" class="w-8 h-8" />
                  </div>
                  <div class="w-auto mx-auto text-center mt-2">Cargando...</div>
                </Table.Td>
              </Table.Tr>
              <Table.Tr v-else-if="sinResultados">
                <Table.Td colspan="5" class="text-center py-8 text-slate-500">
                  No hay registros para los filtros elegidos.
                </Table.Td>
              </Table.Tr>
              <Table.Tr v-for="row in logStore.logsConsulta" :key="row.id">
                <Table.Td class="whitespace-nowrap">{{
                  formatoFechaHora(row.fecha)
                }}</Table.Td>
                <Table.Td class="whitespace-nowrap">{{
                  etiquetaModulo(row.modulo)
                }}</Table.Td>
                <Table.Td>{{
                  usuariosStore.getById(row.usuario)?.nombre
                }}</Table.Td>

                <div v-if="!isString(row.accion)">
                  <tr class="text-sm" v-for="log in row.accion">
                    <Table.Td>{{ log }}</Table.Td>
                  </tr>
                </div>
                <div v-else class="text-sm">
                  <Table.Td>{{ row.accion }}</Table.Td>
                </div>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div>
      </Preview.Panel>
    </div>
  </Preview>
</template>
